/**
 * Create an authenticated fetch helper bound to a Supabase client.
 *
 * @param {object} options
 * @param {import('@supabase/supabase-js').SupabaseClient} options.supabase
 * @param {string} [options.baseUrl=''] - Prefixed to string URLs that start with "/". The web app
 *   leaves this empty (same-origin /api proxy); native apps must pass the backend origin.
 * @param {() => Record<string, string>} [options.getDefaultHeaders] - Extra headers added to every
 *   request unless the caller already set them.
 */
export function createApiClient({ supabase, baseUrl = '', getDefaultHeaders } = {}) {
  const normalizedBaseUrl = String(baseUrl || '').replace(/\/$/, '');

  /**
   * Retrieve a valid access token, refreshing the Supabase session if required.
   * @param {boolean} forceRefresh - Forces a refresh token flow when true.
   * @returns {Promise<string>} - Bearer token string.
   */
  async function getAccessToken(forceRefresh = false) {
    if (forceRefresh) {
      const { data, error } = await supabase.auth.refreshSession();
      if (error) throw error;
      const refreshedToken = data?.session?.access_token;
      if (refreshedToken) return refreshedToken;
    } else {
      const { data } = await supabase.auth.getSession();
      const existingToken = data?.session?.access_token;
      if (existingToken) return existingToken;
    }

    // Final attempt — force refresh when we did not have a token initially.
    const { data: refreshed, error: refreshError } = await supabase.auth.refreshSession();
    if (refreshError) throw refreshError;
    const token = refreshed?.session?.access_token;
    if (!token) {
      throw new Error('No active session. Please sign in again.');
    }
    return token;
  }

  function resolveInput(input) {
    if (normalizedBaseUrl && typeof input === 'string' && input.startsWith('/')) {
      return `${normalizedBaseUrl}${input}`;
    }
    return input;
  }

  /**
   * Perform an authenticated fetch request that automatically attaches the Supabase session token
   * and retries once on 401 responses after refreshing the session.
   *
   * @param {string | Request} input - fetch URL or Request object
   * @param {object} options
   * @param {'json'|'text'|'raw'} [options.parseAs='json'] - Desired response format
   * @param {boolean} [options.retryOnUnauthorized=true] - Whether to retry after a 401
   * @param {number} [options.timeoutMs=0] - Abort the request after this many milliseconds
   * @returns {Promise<any>} Parsed response according to parseAs option
   */
  async function fetchWithAuth(input, options = {}) {
    const {
      parseAs = 'json',
      retryOnUnauthorized = true,
      timeoutMs = 0,
      ...fetchOptions
    } = options;

    const url = resolveInput(input);
    const mergedHeaders = new Headers(fetchOptions.headers || {});
    const defaultHeaders = getDefaultHeaders ? getDefaultHeaders() : {};
    Object.entries(defaultHeaders).forEach(([key, value]) => {
      if (!mergedHeaders.has(key) && value) {
        mergedHeaders.set(key, value);
      }
    });

    // Ensure JSON requests send the correct content-type.
    // If callers pass a JSON string body (common), browsers default to text/plain unless we set it.
    // Do NOT set content-type for FormData/Blob/etc.
    try {
      const hasBody = fetchOptions.body !== undefined && fetchOptions.body !== null;
      const hasContentType = mergedHeaders.has('Content-Type');
      const body = fetchOptions.body;
      const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;
      const isBlob = typeof Blob !== 'undefined' && body instanceof Blob;
      const isArrayBuffer = typeof ArrayBuffer !== 'undefined' && body instanceof ArrayBuffer;
      const isUrlSearchParams = typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams;

      if (hasBody && !hasContentType && !isFormData && !isBlob && !isArrayBuffer && !isUrlSearchParams) {
        if (typeof body === 'string') {
          const trimmed = body.trim();
          if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
            mergedHeaders.set('Content-Type', 'application/json');
          }
        } else if (typeof body === 'object') {
          // If a plain object somehow gets passed, serialize it to JSON.
          fetchOptions.body = JSON.stringify(body);
          mergedHeaders.set('Content-Type', 'application/json');
        }
      }
    } catch {
      // best-effort; never block the request
    }

    let token = await getAccessToken();
    mergedHeaders.set('Authorization', `Bearer ${token}`);

    const doFetch = async () => {
      if (!timeoutMs || Number(timeoutMs) <= 0) {
        return fetch(url, {
          ...fetchOptions,
          headers: mergedHeaders,
        });
      }

      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), Number(timeoutMs));
      try {
        return await fetch(url, {
          ...fetchOptions,
          headers: mergedHeaders,
          signal: controller.signal,
        });
      } finally {
        clearTimeout(timer);
      }
    };

    let response = await doFetch();

    if (response.status === 401 && retryOnUnauthorized) {
      token = await getAccessToken(true);
      mergedHeaders.set('Authorization', `Bearer ${token}`);
      response = await doFetch();
    }

    if (!response.ok) {
      let errorPayload;
      try {
        errorPayload = await response.clone().json();
      } catch {
        errorPayload = await response.text();
      }

      const error = new Error(
        errorPayload?.error ||
          errorPayload?.message ||
          `Request failed with status ${response.status}`
      );
      error.status = response.status;
      error.payload = errorPayload;
      throw error;
    }

    return parseResponse(response, parseAs);
  }

  return { fetchWithAuth, getAccessToken };
}

/**
 * Parse the fetch Response according to the requested output format.
 * @param {Response} response
 * @param {'json'|'text'|'raw'} parseAs
 * @returns {Promise<unknown>|Response}
 */
async function parseResponse(response, parseAs = 'json') {
  if (parseAs === 'raw') return response;
  if (parseAs === 'text') return response.text();
  // Default to JSON parsing
  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return null;
  }
  return response.json();
}
